import { isDocsUrl, urlToContentPath } from '~/utils/routeMapping';

/**
 * Extracts the direct children of a site node (categories in flat mode,
 * sub-sites in subsite mode) for a specific locale+site from a full nav tree.
 */
export function extractSiteChildren(fullNav: any[], locale: string, site: string): any[] {
  const localeRoot = fullNav.find((n: any) => n.path === `/${locale}`);
  const docsNode = localeRoot?.children?.find((n: any) => n.path === `/${locale}/docs`);
  const sitePath = `/${locale}/docs/${site}`;
  const siteNode = docsNode?.children?.find((n: any) => n.path === sitePath);
  return siteNode?.children?.filter((c: any) => c.path !== sitePath) ?? [];
}

/**
 * Parsed inputs for a locale switch — the abstraction layer that resolvers
 * read from. Resolvers should not reach back into raw segment indexing.
 */
export interface LocaleSwitchContext {
  currentUrlPath: string;
  newLocale: string;
  inDocs: boolean;
  currentPath: string;
  currentLocale: string;
  site: string;
  seg3: string | undefined;
  seg4: string | undefined;
  parts: string[];
}

/**
 * Build the locale-switch context from the raw URL inputs.
 */
export function parseLocaleSwitchContext(
  currentUrlPath: string,
  newLocale: string,
): LocaleSwitchContext {
  const inDocs = isDocsUrl(currentUrlPath);
  const currentPath = urlToContentPath(currentUrlPath);
  const parts = currentPath.split('/');
  // Commercial: ['', locale, site, seg3?, seg4?]
  // Docs:       ['', locale, 'docs', site, seg3?, seg4?] — shift past the
  // /docs/ segment so `site` is the real sub-site (not the literal "docs",
  // which mapped to nonexistent /{locale}/docs/docs and 404'd every docs
  // language switch).
  const currentLocale = parts[1] ?? '';
  const site = inDocs ? (parts[3] ?? '') : (parts[2] ?? '');
  const seg3 = inDocs ? parts[4] : parts[3];
  const seg4 = inDocs ? parts[5] : parts[4];
  return {
    currentUrlPath,
    newLocale,
    inDocs,
    currentPath,
    currentLocale,
    site,
    seg3,
    seg4,
    parts,
  };
}
